import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useDocumentStore, ProcessingStage } from '@/stores/useDocumentStore';
import { useProductOrderStore } from '@/domain/product-orders/stores/useProductOrderStore';
import { ProductOrder } from '@/domain/product-orders/types/product-order';
import { httpRequest, uploadFile } from '@/lib/http-client';
import { authService } from '@/lib/auth-service';
import { captureEvent, captureException } from '@/config/posthog';
import { captureException as sentryCaptureException } from '@/config/sentry';
import { createLogger } from '@/lib/logging';

const logger = createLogger('DocumentProcessing');
const POLL_MS = 3_000;
const MAX_SILENCE_MS = 60_000;
const REQUEST_TIMEOUT_MS = 15_000;

interface RunFile { documentId: string; fileName: string; status: string; error?: string }
interface VerificationRun { id: string; status: string; files: RunFile[]; error: string | null; final_snapshot_id: string | null }

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
const status = (error: unknown) => (error as { response?: { status?: number } })?.response?.status;
class RunWatchError extends Error {
  constructor() {
    super('The verification status could not be loaded. It may still finish. Reopen this order to check its progress.');
  }
}

// A missing run is normal for older orders. After an uncertain start response,
// allow the accepted request to appear, and only reconnect to its documents.
async function latestRunState(orderId: string, isCurrent: () => boolean, documentIds?: string[]): Promise<VerificationRun | null> {
  const giveUpAt = Date.now() + MAX_SILENCE_MS;
  while (isCurrent()) {
    try {
      const state = await httpRequest<VerificationRun>(`/api/orders/${encodeURIComponent(orderId)}/runs/latest`, {
        timeout: Math.min(REQUEST_TIMEOUT_MS, Math.max(1, giveUpAt - Date.now())),
      });
      if (!documentIds || (state.files.length === documentIds.length && state.files.every(file => documentIds.includes(file.documentId)))) {
        return state;
      }
    } catch (error) {
      const code = status(error);
      if (code === 404 && !documentIds) return null;
      if (code && code >= 400 && code < 500 && code !== 404) throw new RunWatchError();
    }
    if (Date.now() >= giveUpAt) throw new RunWatchError();
    await delay(Math.min(POLL_MS, giveUpAt - Date.now()));
  }
  return null;
}

export function useDocumentProcessing() {
  const { setProcessing, setResults, setExtractedEntities, setDocumentMetadata, setCurrentReviewStage, setConsolidationResult } = useDocumentStore();
  const { currentOrder } = useProductOrderStore();
  const [progress, setProgress] = useState(0);
  const activeRun = useRef(0);
  const watchedOrder = useRef<string | null>(null);
  useEffect(() => () => { activeRun.current++; watchedOrder.current = null; }, []);

  const reportError = useCallback((error: unknown, order: ProductOrder, isCurrent: () => boolean) => {
    if (!isCurrent()) return;
    const err = error instanceof Error ? error : new Error('Verification could not run');
    logger.error('Verification could not run', { error: err.message });
    captureException(err, { orderId: order.id });
    sentryCaptureException(err, { orderId: order.id, stage: 'verification_run' });
    toast.error(err.message);
    setDocumentMetadata({ processingStage: ProcessingStage.ERROR, processingErrors: [err.message] });
  }, [setDocumentMetadata]);

  const finishWatching = useCallback((isCurrent: () => boolean) => {
    if (isCurrent()) {
      watchedOrder.current = null;
      setProcessing(false);
      setCurrentReviewStage(null);
    }
  }, [setProcessing, setCurrentReviewStage]);

  const watchRun = useCallback(async (initial: VerificationRun, order: ProductOrder, isCurrent: () => boolean) => {
    let state = initial;
    while (isCurrent() && state.status === 'running') {
      const done = state.files.filter(file => !['pending', 'processing'].includes(file.status)).length;
      const scaled = 10 + (done / Math.max(1, state.files.length)) * 85;
      setProcessing(true);
      setProgress(scaled);
      setDocumentMetadata({ totalFiles: state.files.length, currentFileIndex: done, processingProgress: scaled, processingStage: done === state.files.length ? ProcessingStage.CONSOLIDATION : ProcessingStage.OCR, fileName: state.files.find(file => file.status === 'processing')?.fileName ?? 'Verifying documents' });
      await delay(POLL_MS);
      if (!isCurrent()) return;
      const next = await latestRunState(order.id, isCurrent);
      if (!isCurrent()) return;
      if (!next) throw new RunWatchError();
      state = next;
    }
    if (!isCurrent()) return;
    if (state.final_snapshot_id) {
      const token = await authService.getToken();
      if (!isCurrent()) return;
      if (!token) throw new Error('Please sign in again to see the results.');
      await useProductOrderStore.getState().loadHistoricalOrder(order, token, isCurrent);
      if (!isCurrent()) return;
    }
    if (state.status === 'failed' || state.status === 'interrupted' || !state.final_snapshot_id) {
      const reasons = [state.error, ...state.files.filter(file => file.error).map(file => `${file.fileName}: ${file.error}`)].filter(Boolean) as string[];
      setDocumentMetadata({ processingStage: ProcessingStage.ERROR, processingErrors: reasons.length ? reasons : ['Verification did not finish. Run it again.'] });
    }
    setProgress(100);
    captureEvent('client_document_processing_completed', { fileCount: state.files.length, orderId: order.id, status: state.status });
  }, [setProcessing, setDocumentMetadata]);

  const restoreOrder = useCallback(async (order: ProductOrder, token: string, isRequested: () => boolean = () => true) => {
    const run = ++activeRun.current;
    watchedOrder.current = order.id;
    useProductOrderStore.getState().setCurrentOrder(order);
    const isCurrent = () => activeRun.current === run && useProductOrderStore.getState().currentOrder?.id === order.id;
    const isRestoring = () => isCurrent() && isRequested();
    try {
      const state = await latestRunState(order.id, isRestoring);
      if (!isRestoring()) return;
      if (!state) {
        await useProductOrderStore.getState().loadHistoricalOrder(order, token, isCurrent);
        finishWatching(isCurrent);
      } else if (state.status === 'running') {
        // Release the initial loading screen as soon as we can show progress.
        void watchRun(state, order, isCurrent)
          .catch(error => reportError(error, order, isCurrent))
          .finally(() => finishWatching(isCurrent));
      } else {
        await watchRun(state, order, isCurrent);
        finishWatching(isCurrent);
      }
    } catch (error) {
      reportError(error, order, isCurrent);
      finishWatching(isCurrent);
    }
  }, [watchRun, reportError, finishWatching]);

  const processDocuments = useCallback(async (files: File[]) => {
    const existing = useDocumentStore.getState();
    if (files.length === 0 || existing.isProcessing || existing.results.length > 0 || existing.consolidationResult) return;
    const order = currentOrder;
    if (!order?.id) {
      toast.error('Create an order before verifying documents.');
      return;
    }
    const run = ++activeRun.current;
    watchedOrder.current = order.id;
    const isCurrent = () => activeRun.current === run && useProductOrderStore.getState().currentOrder?.id === order.id;
    captureEvent('client_document_processing_started', { fileCount: files.length, orderId: order.id });
    setProcessing(true);
    setProgress(0);
    setResults([]);
    setExtractedEntities([]);
    setConsolidationResult(null);
    setDocumentMetadata({ totalFiles: files.length, currentFileIndex: 0, processingProgress: 0, processingErrors: [], processingStage: ProcessingStage.OCR, fileName: 'Uploading documents' });
    try {
      const documentIds: string[] = [];
      for (const [index, file] of files.entries()) {
        const stored = await uploadFile<{ id: string }>(`/api/order-history/documents/${encodeURIComponent(order.id)}`, file);
        if (!isCurrent()) return;
        documentIds.push(stored.id);
        setProgress(((index + 1) / files.length) * 10);
      }
      let state: VerificationRun | null;
      try {
        state = await httpRequest<VerificationRun>(`/api/orders/${encodeURIComponent(order.id)}/runs`, {
          method: 'POST', data: { documentIds }, timeout: REQUEST_TIMEOUT_MS,
        });
      } catch (error) {
        const code = status(error);
        if (code && code >= 400 && code < 500) throw error;
        // The server may have accepted it. Reconcile; never repeat the POST.
        state = await latestRunState(order.id, isCurrent, documentIds);
      }
      if (!isCurrent()) return;
      if (!state) throw new RunWatchError();
      await watchRun(state, order, isCurrent);
    } catch (error) {
      reportError(error, order, isCurrent);
    } finally {
      finishWatching(isCurrent);
    }
  }, [currentOrder, setProcessing, setResults, setExtractedEntities, setDocumentMetadata, setConsolidationResult, watchRun, reportError, finishWatching]);

  return { progress, processDocuments, restoreOrder, isWatchingOrder: (orderId: string) => watchedOrder.current === orderId };
}
